import type { Direction, Point } from "@/core/types";
import type { PortalDefinition, PortalNetwork } from "./types";

export function cellKey(p: Point): string {
  return `${p.x},${p.y}`;
}

export function pointsEqual(a: Point, b: Point): boolean {
  return a.x === b.x && a.y === b.y;
}

const STEP_BY_DIRECTION: Record<Direction, Point> = {
  up: { x: 0, y: -1 },
  down: { x: 0, y: 1 },
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 },
};

export function stepInDirection(point: Point, direction: Direction): Point {
  const d = STEP_BY_DIRECTION[direction];
  return { x: point.x + d.x, y: point.y + d.y };
}

/** The direction opposite `direction` (up<->down, left<->right). */
export function oppositeDirection(direction: Direction): Direction {
  switch (direction) {
    case "up":
      return "down";
    case "down":
      return "up";
    case "left":
      return "right";
    case "right":
      return "left";
  }
}

/**
 * Builds a straight-line snake body, head first, starting at `head` and
 * extending backwards (opposite of `direction`) for `length` segments.
 * Shared by the rules engine and the level validator so both agree on
 * what a level's starting snake looks like.
 */
export function buildInitialSnakeBody(head: Point, direction: Direction, length: number): Point[] {
  const back = oppositeDirection(direction);
  const body: Point[] = [];
  let cursor = head;
  for (let i = 0; i < length; i++) {
    body.push(cursor);
    cursor = stepInDirection(cursor, back);
  }
  return body;
}

// ---------------------------------------------------------------------
// Level-authoring helpers: shared by every `worldN.ts` so obstacle shapes
// never have to be hand-listed cell by cell.
// ---------------------------------------------------------------------

export function verticalWall(x: number, yFrom: number, yTo: number): Point[] {
  const points: Point[] = [];
  for (let y = yFrom; y <= yTo; y++) points.push({ x, y });
  return points;
}

export function horizontalWall(y: number, xFrom: number, xTo: number): Point[] {
  const points: Point[] = [];
  for (let x = xFrom; x <= xTo; x++) points.push({ x, y });
  return points;
}

/** A filled rectangle of cells, inclusive of both corners. */
export function rect(xFrom: number, yFrom: number, xTo: number, yTo: number): Point[] {
  const points: Point[] = [];
  for (let y = yFrom; y <= yTo; y++) {
    for (let x = xFrom; x <= xTo; x++) points.push({ x, y });
  }
  return points;
}

/** Merges any number of point groups, de-duplicating by cell. */
export function mergeUnique(...groups: Point[][]): Point[] {
  const seen = new Set<string>();
  const merged: Point[] = [];
  for (const group of groups) {
    for (const point of group) {
      const key = cellKey(point);
      if (!seen.has(key)) {
        seen.add(key);
        merged.push(point);
      }
    }
  }
  return merged;
}

/** Removes any cells in `points` that also appear in `remove` (e.g. carving a gap out of a wall). */
export function subtract(points: Point[], remove: Point[]): Point[] {
  const removeKeys = new Set(remove.map(cellKey));
  return points.filter((p) => !removeKeys.has(cellKey(p)));
}

// ---------------------------------------------------------------------
// Portals V1 (Saturn World): shared between `LevelGame` (movement
// resolution) and `LevelValidator`/`biteSpawn` (structural checks, spawn
// blocklist) so none of them can ever disagree about which cell resolves
// to which, or which cells are portal cells at all.
// ---------------------------------------------------------------------

/**
 * True for a `PortalNetwork` (3+ endpoints, random destination), false for
 * a `PortalPair` (fixed two-cell link) - the one place this distinction is
 * actually checked. Structural (presence of `endpoints`), not a shared
 * discriminant field, so every existing `{id, a, b}` pair literal needs no
 * tag of its own.
 */
export function isPortalNetwork(def: PortalDefinition): def is PortalNetwork {
  return "endpoints" in def;
}

/** Every cell belonging to one portal definition - `[a, b]` for a pair, `endpoints` (as-authored) for a network. */
export function portalDefinitionEndpoints(def: PortalDefinition): Point[] {
  return isPortalNetwork(def) ? def.endpoints : [def.a, def.b];
}

/**
 * Every portal endpoint, flattened across every definition, in authoring
 * order. `[]` for `undefined`/empty `portals` - every existing level with
 * no `portals` field behaves exactly as before.
 */
export function portalEndpoints(portals: readonly PortalDefinition[] | undefined): Point[] {
  if (!portals) return [];
  return portals.flatMap(portalDefinitionEndpoints);
}

/**
 * One endpoint's resolution target. `"fixed"` (a `PortalPair` endpoint)
 * resolves deterministically to `to`, the other endpoint of its own pair -
 * byte-for-byte the original Portals V1 behavior. `"random"` (a
 * `PortalNetwork` endpoint) carries `candidates` - every OTHER endpoint in
 * its own network, with the entered endpoint itself already excluded -
 * and leaves the actual pick to the caller (`LevelGame.tick()`, which owns
 * the RNG), so building this lookup stays pure and RNG-free.
 */
export type PortalResolution =
  | { kind: "fixed"; pairId: string; to: Point }
  | { kind: "random"; pairId: string; candidates: Point[] };

/**
 * A cellKey -> resolution lookup built once per level (not per tick) -
 * `resolvePortalTraversal(point)` below is just a map read, the smallest
 * possible per-move cost. Each pair endpoint maps to the OTHER endpoint of
 * its own pair only; each network endpoint maps to every OTHER endpoint of
 * its own network - there is no cell that maps to itself, since
 * `LevelValidator` rejects any level where an endpoint is reused across
 * definitions or duplicated within one.
 */
export function buildPortalLookup(portals: readonly PortalDefinition[] | undefined): Map<string, PortalResolution> {
  const map = new Map<string, PortalResolution>();
  if (!portals) return map;
  for (const def of portals) {
    if (isPortalNetwork(def)) {
      for (const endpoint of def.endpoints) {
        const candidates = def.endpoints.filter((e) => !pointsEqual(e, endpoint));
        map.set(cellKey(endpoint), { kind: "random", pairId: def.id, candidates });
      }
    } else {
      map.set(cellKey(def.a), { kind: "fixed", pairId: def.id, to: def.b });
      map.set(cellKey(def.b), { kind: "fixed", pairId: def.id, to: def.a });
    }
  }
  return map;
}

/**
 * Resolves `point` through `lookup` exactly once - `null` if `point` isn't
 * a portal endpoint. Deliberately a single lookup, never a loop: the
 * returned resolution is never itself looked up again in the same call, so
 * portal chains/recursive resolution are structurally impossible here,
 * independent of whatever the authored level data happens to contain (this
 * holds for a `"random"` resolution too - the caller picks ONE of
 * `candidates`, that pick is never itself re-resolved).
 */
export function resolvePortalTraversal(point: Point, lookup: ReadonlyMap<string, PortalResolution>): PortalResolution | null {
  return lookup.get(cellKey(point)) ?? null;
}

// ---------------------------------------------------------------------
// Forward-opening safety (PR24 - Daily Foundations hardening): a
// reusable, generic primitive for a rule we are NOT enforcing yet - "the
// first N forward cells from an arbitrary spawn position/direction must
// be safe" - see this PR's own description for why this exists ahead of
// actually requiring it. Deliberately relative to `snakeStart` +
// `startDirection` ONLY: never assumes a left-side spawn or a
// right-facing snake, works identically for any in-bounds spawn cell and
// any of the four cardinal directions.
// ---------------------------------------------------------------------

export interface ForwardOpeningSafetyInput {
  grid: { cols: number; rows: number };
  snakeStart: Point;
  startDirection: Direction;
  initialLength: number;
  obstacles: readonly Point[];
  /** Fixed lethal cells (see `LevelDefinition.blackHoles`'s doc) - defaults to none. */
  blackHoles?: readonly Point[];
  /** Fixed or randomized portal cells (see `LevelDefinition.portals`'s doc) - defaults to none. */
  portals?: readonly PortalDefinition[];
  /**
   * Any additional forced/lethal cell a future hazard type introduces
   * that isn't yet modeled by `blackHoles`/`portals` above - kept
   * separate and generic so a brand-new hazard kind never has to be
   * threaded through this function's own signature to be checked; the
   * caller computes and supplies its cells directly. Defaults to none.
   */
  extraHazardCells?: readonly Point[];
}

export interface ForwardOpeningSafetyResult {
  safe: boolean;
  /** The forward cells actually inspected, closest to the head first, in travel order - `distance` cells long when every one is safe, or shorter when inspection stopped early at the first unsafe cell (cells beyond it are never appended/inspected). */
  cellsChecked: Point[];
  /** The first cell (in travel order) that failed a safety check, or `null` when every requested cell was safe. */
  firstUnsafeCell: Point | null;
  /** Why `firstUnsafeCell` failed ("out of bounds" | "obstacle" | "initial snake body" | "black hole" | "portal endpoint" | "extra hazard"), or `null` when `safe` is `true`. */
  reason: string | null;
}

/**
 * Checks whether the `distance` cells immediately ahead of `snakeStart`,
 * in `startDirection`, are all safe: in bounds, not an obstacle, not part
 * of the initial snake body, not a BLACK HOLE cell, not a portal
 * endpoint, and not any caller-supplied `extraHazardCells`. Stops at the
 * first unsafe cell found - closer cells are always checked before
 * farther ones, and nothing beyond the first failure is inspected.
 *
 * NOT wired into any generation/validation acceptance criteria yet -
 * this is a standalone primitive a future rule can call with whatever
 * `distance` it needs (e.g. `4`); it does not change what
 * `validateDailyChallengeSpec()`/`validateLevel()` currently accept.
 */
export function checkForwardOpeningSafety(input: ForwardOpeningSafetyInput, distance: number): ForwardOpeningSafetyResult {
  const { grid, snakeStart, startDirection, initialLength, obstacles, blackHoles = [], portals, extraHazardCells = [] } = input;
  const inBounds = (p: Point) => p.x >= 0 && p.x < grid.cols && p.y >= 0 && p.y < grid.rows;

  const bodyKeys = new Set(buildInitialSnakeBody(snakeStart, startDirection, initialLength).map(cellKey));
  const obstacleKeys = new Set(obstacles.map(cellKey));
  const blackHoleKeys = new Set(blackHoles.map(cellKey));
  const portalEndpointKeys = new Set(portalEndpoints(portals).map(cellKey));
  const extraHazardKeys = new Set(extraHazardCells.map(cellKey));

  const cellsChecked: Point[] = [];
  let cursor = snakeStart;
  for (let i = 0; i < distance; i++) {
    cursor = stepInDirection(cursor, startDirection);
    cellsChecked.push(cursor);

    if (!inBounds(cursor)) {
      return { safe: false, cellsChecked, firstUnsafeCell: cursor, reason: "out of bounds" };
    }
    const key = cellKey(cursor);
    if (obstacleKeys.has(key)) {
      return { safe: false, cellsChecked, firstUnsafeCell: cursor, reason: "obstacle" };
    }
    if (bodyKeys.has(key)) {
      return { safe: false, cellsChecked, firstUnsafeCell: cursor, reason: "initial snake body" };
    }
    if (blackHoleKeys.has(key)) {
      return { safe: false, cellsChecked, firstUnsafeCell: cursor, reason: "black hole" };
    }
    if (portalEndpointKeys.has(key)) {
      return { safe: false, cellsChecked, firstUnsafeCell: cursor, reason: "portal endpoint" };
    }
    if (extraHazardKeys.has(key)) {
      return { safe: false, cellsChecked, firstUnsafeCell: cursor, reason: "extra hazard" };
    }
  }

  return { safe: true, cellsChecked, firstUnsafeCell: null, reason: null };
}
